import React from "react";
import { Link } from "react-router-dom";
import { useSeo } from "@/hooks/useSeo";
import { SITE } from "@/data/config";
import { Overline, Crumbs } from "@/components/Primitives";
import { organizationSchema, websiteSchema, webPageSchema, breadcrumbSchema } from "@/lib/schemas";

// Human-readable sitemap. Mirrors public/sitemap.xml for visitors.
const SECTIONS = [
  {
    h: "Primary",
    links: [
      { to: "/", label: "Home" },
      { to: "/about", label: "About" },
      { to: "/why-kostaja", label: "Why Kostaja" },
      { to: "/best-brand-marketing-agency-canada", label: "How to choose an agency in Canada" },
      { to: "/service-business-marketing-canada", label: "Service business marketing" },
      { to: "/local-marketing-systems", label: "Local marketing systems" },
      { to: "/ai-search-visibility-for-service-businesses", label: "AI search visibility" },
      { to: "/website-branding-social-ai-for-service-businesses", label: "Website, brand, social, AI" },
    ],
  },
  {
    h: "Services",
    links: [
      { to: "/services", label: "All services" },
      { to: "/services/brand-strategy", label: "Brand strategy" },
      { to: "/services/website-design", label: "Website design" },
      { to: "/services/social-media-marketing", label: "Social media marketing" },
      { to: "/services/google-business-profile-local-seo", label: "Google Business Profile and local SEO" },
      { to: "/services/ai-receptionist", label: "AI receptionist" },
      { to: "/services/print-sales-materials", label: "Print and sales materials" },
      { to: "/services/marketing-systems", label: "Marketing systems" },
    ],
  },
  {
    h: "Discover",
    links: [
      { to: "/industries", label: "Industries" },
      { to: "/needs", label: "Browse by need" },
      { to: "/portfolio", label: "Portfolio" },
      { to: "/blog", label: "Blog" },
      { to: "/radio", label: "Radio Kostaja" },
      { to: "/press", label: "Press" },
      { to: "/service-areas", label: "Service areas" },
    ],
  },
  {
    h: "Talk",
    links: [
      { to: "/contact", label: "Contact" },
      { to: "/book", label: "Book a strategy call" },
    ],
  },
  {
    h: "Legal",
    links: [
      { to: "/legal/privacy", label: "Privacy" },
      { to: "/legal/terms", label: "Terms" },
      { to: "/legal/refund-policy", label: "Refund policy" },
      { to: "/legal/accessibility", label: "Accessibility" },
    ],
  },
];

const Sitemap = () => {
  useSeo({
    title: "Sitemap | Kostaja Ltd.",
    description: "Every public page on kostaja.ca in one place.",
    canonical: `${SITE.url}/sitemap`,
    jsonLd: [
      organizationSchema(),
      websiteSchema(),
      webPageSchema({ path: "/sitemap", name: "Sitemap | Kostaja Ltd.", description: "Every public page on kostaja.ca in one place." }),
      breadcrumbSchema([
        { name: "Home", path: "/" },
        { name: "Sitemap", path: "/sitemap" },
      ]),
    ],
  });
  return (
    <main data-testid="sitemap-page" className="bg-black text-white">
      <section className="border-b border-white/10">
        <div className="max-w-[1440px] mx-auto px-5 lg:px-10 py-14 lg:py-24">
          <Crumbs items={[{ to: "/", label: "Home" }, { label: "Sitemap" }]} />
          <Overline className="mt-8">Sitemap</Overline>
          <h1 className="mt-4 text-5xl sm:text-6xl lg:text-[80px] font-black tracking-tight leading-[0.92]">
            Every public page in one place.
          </h1>
          <p className="mt-6 text-white/75 text-base lg:text-lg max-w-2xl">
            The full public index of kostaja.ca. Machines can crawl the XML at{" "}
            <a href="/sitemap.xml" className="underline underline-offset-4 hover:text-[#FFC600]">
              /sitemap.xml
            </a>
            . Humans can browse below.
          </p>
        </div>
      </section>

      <section className="border-b border-white/10">
        <div className="max-w-[1440px] mx-auto px-5 lg:px-10 py-14 lg:py-20 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
          {SECTIONS.map((sec) => (
            <div key={sec.h}>
              <Overline className="mb-4">{sec.h}</Overline>
              <ul className="space-y-2">
                {sec.links.map((l) => (
                  <li key={l.to}>
                    <Link
                      to={l.to}
                      className="k-link text-white/85 hover:text-[#FFC600] text-base"
                    >
                      {l.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>
    </main>
  );
};

export default Sitemap;
